import type {
  FilterItem,
  PaginationParams,
} from "@components/TableFilters/FiltersPanel/FiltersPanel.types";
import {
  type BunkerBargeDetailsPayload,
  type Vessel,
  type VesselRequestPayload,
  type VesselsResponse,
  type VesselStatusPayload,
} from "@services/vessel/types";
import { transformFiltersToPayload } from "@utils/filters";
import { api } from "./api";

export interface VesselsParams {
  pageSize?: number;
  pageNumber?: number;
  imoNumber?: string;
  vesselName?: string;
}

const BFF_VESSEL = "bff-vessel";

export const getSearchVessels = async (
  service: string,
  paginationParams: PaginationParams,
  filters: FilterItem[],
) => {
  return api.post<VesselsResponse>(
    service,
    "vessels:search",
    transformFiltersToPayload(paginationParams, filters),
  );
};

export const getScanVessels = async (
  service: string,
  value: string,
  pageNumber: number,
  pageSize: number,
) => {
  return api.get<VesselsResponse>(service, "vessels:scan", {
    params: {
      imoOrName: value,
      pageNumber,
      pageSize,
    },
  });
};

export const getVessels = async (service: string, params: VesselsParams, signal?: AbortSignal) => {
  return api.get<VesselsResponse>(service, "vessels", { params, signal: signal });
};

export const getVesselById = async (service: string, id?: string) => {
  return api.get<Vessel>(service, `vessels/${id}`);
};

//EnforceVesselRegistrationAccess was created because of this ticket POFDV001-8979
export const getVesselByIdNoCompanyIdValidation = async (
  service: string,
  id?: string,
  enforceVesselRegistrationAccess: boolean = false,
) => {
  return api.get<Vessel>(service, `vessels/${id}:without-company-id-filtering`, {
    params: {
      enforceVesselRegistrationAccess,
    },
  });
};

export const postVessel = async ({
  vesselPayload,
  transactionId,
}: {
  vesselPayload: VesselRequestPayload;
  transactionId?: string;
}) => {
  return api.post<VesselRequestPayload>(
    BFF_VESSEL,
    "vessels",
    vesselPayload,
    ...(transactionId ? [{ transactionId }] : []),
  );
};

export const putVessel = async ({
  id,
  vesselPayload,
  transactionId,
}: {
  id: string;
  vesselPayload: VesselRequestPayload;
  transactionId?: string;
}) => {
  return api.put(
    BFF_VESSEL,
    `vessels/${id}`,
    vesselPayload,
    ...(transactionId ? [{ transactionId }] : []),
  );
};

export const postDraftVessel = async ({
  vesselPayload,
  transactionId,
}: {
  vesselPayload: VesselRequestPayload;
  transactionId?: string;
}) => {
  return api.post<VesselRequestPayload>(
    BFF_VESSEL,
    "vessels/drafts",
    vesselPayload,
    ...(transactionId ? [{ transactionId }] : []),
  );
};

export const patchDraftVessel = async ({
  vesselPayload,
  id,
  transactionId,
}: {
  vesselPayload: VesselRequestPayload;
  id: string;
  transactionId?: string;
}) => {
  return api.patch(
    BFF_VESSEL,
    `vessels/${id}/drafts`,
    vesselPayload,
    ...(transactionId ? [{ transactionId }] : []),
  );
};

export const discardDraftVessel = async ({
  id,
  transactionId,
}: {
  id: string;
  transactionId?: string;
}) => {
  return api.delete(
    BFF_VESSEL,
    `vessels/${id}/drafts`,
    ...(transactionId ? [{ transactionId }] : []),
  );
};

export const updateVesselStatus = async ({
  service,
  id,
  vesselStatusPayload,
  transactionId,
}: {
  service: string;
  id: string;
  vesselStatusPayload: VesselStatusPayload;
  transactionId?: string;
}) => {
  return api.patch(
    service,
    `vessels/${id}/status`,
    vesselStatusPayload,
    ...(transactionId ? [{ transactionId }] : []),
  );
};

export const updateBunkerBarge = async ({
  service,
  id,
  bunkerBargePayload,
  transactionId,
}: {
  service: string;
  id: string;
  bunkerBargePayload: BunkerBargeDetailsPayload;
  transactionId?: string;
}) => {
  return api.patch(
    service,
    `vessels/${id}/update-bb`,
    bunkerBargePayload,
    ...(transactionId ? [{ transactionId }] : []),
  );
};
